<script setup lang="ts">
import { storeToRefs } from 'pinia';
import Dialog from 'primevue/dialog';
import Fieldset from 'primevue/fieldset';
import Select from 'primevue/select';
import Button from 'primevue/button';
import InputNumber from 'primevue/inputnumber';
import Checkbox from 'primevue/checkbox';
import { InputGroup, InputGroupAddon } from 'primevue';
import BulkContent, { type BulkProductContentModel } from './BulkContent.vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, ref } from 'vue';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { isBulkStorage, isLogYard } from 'shared/storageAreas/domain/storageAreaType';
import { useMoveApi, type MoveBulkContent, type MoveRequest as MoveRequest } from '@/moves/api/moveApi';
import { Assert } from 'shared/assert';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useProductStore } from 'shared/products/stores/productStore';
import { convertToDisplayUnit, convertToTon, Unit } from 'shared/common/domain/bulkUnits.ts';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const storageAreaStore = useStorageAreaStore();
const productStore = useProductStore();
const moveApi = useMoveApi();
const actionFailedAlert = useActionFailedAlert();

defineExpose({
  show
});

const visible = ref(false);
const loading = ref(false);
const empty = ref(false);

const fromStorageAreaId = ref<string>();
const fromContent = ref<BulkProductContentModel[]>([]);

const toStorageAreaId = ref<string>();
const toContent = ref<BulkProductContentModel[]>([]);

const moveContent = ref<MoveContent[]>([]);

const moveRequest = computed(() => {
    const fromStorageAreaIdValue = fromStorageAreaId.value;
    if (!fromStorageAreaIdValue) return;

    const toStorageAreaIdValue = toStorageAreaId.value;
    if (!toStorageAreaIdValue) return;

    if (fromStorageAreaIdValue === toStorageAreaIdValue) return;

    const contents: MoveBulkContent[] = [];
    let moveCount = 0;

    for (const productContent of moveContent.value) {
      const value = productContent.amount;
      moveCount += value;
      const product = productStore.getOrThrow(productContent.productId);
      Assert.definied(product.bulkData);
      contents.push({
        ton: convertToTon(value, product.bulkData.unit, product.bulkData.density),
        productId: productContent.productId,
      });
    }

    if (moveCount === 0) return;

    const request: MoveRequest = {
      fromStorageAreaId: fromStorageAreaIdValue,
      toStorageAreaId: toStorageAreaIdValue,
      bulkContents: contents,
      empty: empty.value,
    };

    return request;
});

const moveDisabled = computed(() => moveRequest.value ? loading.value : true);

const storageAreas = computed(() => storageAreaStore.storageAreas
  .filter(s => isBulkStorage(s.type))
  .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
);

function createMoveContent(content: BulkProductContentModel[]) : MoveContent[] {

  const result: MoveContent[] = [];

  for (const productContent of content) {
    result.push({
      productId: productContent.productId,
      productName: productContent.productName,
      amount: 0,
      unit: productContent.displayUnit
    });
  }

  return result;
}

function createStorageAreaContent(storageAreaId: string | undefined) : BulkProductContentModel[] {
  const result: BulkProductContentModel[] = [];
  if (!storageAreaId) return result;
  const storageArea = storageAreaStore.getById(storageAreaId);
  if (!storageArea || !storageArea.bulkStorageData) return result;

  for (const content of storageArea.bulkStorageData.content) {
    const product = productStore.getById(content.productId);
    if (!product || !product.bulkData) continue;
    result.push({
      ton: content.ton,
      productId: product.id,
      productName: product.name,
      displayUnit: product.bulkData.unit,
      displayValue: convertToDisplayUnit(content.ton, product.bulkData.unit, product.bulkData.density)
    });
  }

  result.sort((a, b) => a.productName.localeCompare(b.productName, undefined, { numeric: true }));

  return result;
}

interface MoveContent {
  productId: string;
  productName: string;
  amount: number;
  unit: Unit;
}

interface ShowMoveBulkContentDialog {
  fromStorageAreaId: string;
  toStorageAreaId: string;
}

function show(action: ShowMoveBulkContentDialog) {
  reset();
  selectFromStorageArea(action.fromStorageAreaId);
  selectToStorageArea(action.toStorageAreaId);
  visible.value = true;
}

function reset() {
  visible.value = false;
  loading.value = false;
  fromStorageAreaId.value = undefined;
  toStorageAreaId.value = undefined;
}

function onSwapStorageAreas() {
  const newToStorageAreaId = fromStorageAreaId.value;
  const newFromStorageAreaId = toStorageAreaId.value;
  selectFromStorageArea(newFromStorageAreaId);
  selectToStorageArea(newToStorageAreaId);
}

function selectFromStorageArea(id: string|undefined) {
  fromStorageAreaId.value = id;
  fromContent.value = createStorageAreaContent(id);
  moveContent.value = createMoveContent(fromContent.value);
}

function selectToStorageArea(id: string|undefined) {
  toStorageAreaId.value = id;
  toContent.value = createStorageAreaContent(id);
}

async function onMoveClick() {
  try {
    loading.value = true;
    const request = moveRequest.value;
    Assert.definied(request);

    const response = await moveApi.execute(request);

    if (!response.ok) {
      actionFailedAlert.show(response.details);
      return;
    }

    const move = response.move;

    storageAreaStore.add(move.fromStorageArea);
    storageAreaStore.add(move.toStorageArea);

    show({
      fromStorageAreaId: move.fromStorageArea.id,
      toStorageAreaId: move.toStorageArea.id
    });

  } finally {
    loading.value = false;
  }
}

function onBulkContentClick(content: BulkProductContentModel) {
  for (const productContent of moveContent.value) {
    if (productContent.productId !== content.productId) continue;
      productContent.amount = convertToDisplayUnit(content.ton, content.displayUnit, productStore.getBulkDensity(content.productId));
  }
}

function hasChanged(content: MoveContent) {
  return content.amount > 0;
}

</script>

<template>
  <Dialog :header="translations.bulk.moveDialog.title" v-model:visible="visible">
    <div class="move-bulk-content-grid">
      <Fieldset :legend="translations.generic.from">
        <div class="storage-area-field">
          <label>{{ translations.generic.storageArea }}</label>
          <Select autocomplete="off" v-model:modelValue="fromStorageAreaId"
            :options="storageAreas" option-value="id" option-label="name" autofocus @change="(ev) => selectFromStorageArea(ev.value)"></Select>
        </div>

        <BulkContent :products="fromContent" @click="onBulkContentClick"></BulkContent>

      </Fieldset>
      <Fieldset :legend="translations.generic.move">
        <div v-for="content in moveContent" v-bind:key="content.productId" class="field"
          :class="{ changed: hasChanged(content) }">
          <label>{{ content.productName }}</label>
          <InputGroup>
            <InputNumber :min="0" v-model="content.amount"></InputNumber>
            <InputGroupAddon>{{ translations.bulk.bulkUnitShort(content.unit) }}</InputGroupAddon>
          </InputGroup>

        </div>
                  <div class="pick-all-area">
          <Checkbox v-model="empty" inputId="pickAll" :binary="true" />
          <label for="pickAll">{{ translations.dialogs.moveLogYardContent.pickAll }}</label>
        </div>
      </Fieldset>
      <Fieldset :legend="translations.generic.to">
        <div class="storage-area-field">
          <label>{{ translations.generic.storageArea }}</label>
          <Select autocomplete="off" v-model:modelValue="toStorageAreaId"
            :options="storageAreas" option-value="id" option-label="name" autofocus @change="(ev) => selectToStorageArea(ev.value)"></Select>
        </div>

        <BulkContent :products="toContent"></BulkContent>
      </Fieldset>
    </div>
    <template #footer>
      <Button
        severity="secondary"
        :label="translations.generic.swap" @click="onSwapStorageAreas">
      </Button>
      <Button :disabled="moveDisabled"
        :label="translations.generic.move" @click="onMoveClick" :loading="loading"></Button>
    </template>
  </Dialog>
</template>

<style scoped>

.move-bulk-content-grid {
  display: grid;
  grid-template-rows: auto auto auto;
  row-gap: 1rem;
}

.storage-area-field {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: .5rem;
  padding-bottom: .5rem;
}

.pick-all-area {
  margin-top: .5rem;
  display: flex;
  column-gap: 1rem;
  justify-content: left;
  align-items: center;
  padding-block: 0.5rem;
}

</style>
